import React, { useCallback, useEffect, useState } from 'react';
import {
  Box,
  Button,
  Checkbox,
  Dialog,
  DialogActions,
  DialogContent,
  DialogContentText,
  DialogTitle,
  FormControl,
  MenuItem,
  Select,
  Tab,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Tabs,
  TextField,
  Typography,
} from '@mui/material';
import clsx from 'clsx';
import { useNavigate, useSearchParams } from 'react-router-dom';
import * as XLSX from 'xlsx';
import { apiClient } from 'shared/api';
import {
  EDITABLE_LEAD_STATUSES,
  getLeadStatusClassName,
  getLeadStatusLabel,
  LeadStatus,
  normalizeLeadStatus,
} from 'shared/model/constants/lead-status';
import { getLeadCancelReasonLabel } from 'shared/model/constants/lead-cancel-reason';
import {
  formatLeadExportValue,
  isLeadTranslatedExportField,
} from 'shared/model/constants/lead-source';
import { ROUTE_PATHS } from 'shared/model/constants/paths';
import { ButtonCustom } from 'shared/ui/button-custom';
import { DashboardHeaderBackLink } from 'widgets/dashboard-header-back-link';
import styles from './leads-admin.module.scss';

type LeadType = 'selection' | 'insurance' | 'leasing';

interface Lead {
  id: number;
  documentId: string;
  equipment?: string;
  condition?: string;
  objectType?: string;
  region?: string;
  amount?: string;
  deadline?: string;
  name?: string;
  phone?: string;
  comment?: string;
  inn?: string;
  advance?: string;
  form_source?: string;
  consent_at?: string;
  referrer?: string;
  type?: LeadType;
  template?: string;
  path?: string;
  utm_source?: string;
  utm_campaign?: string;
  utm_term?: string;
  yclid?: string;
  ym_uid?: string;
  ym_cookies?: string;
  lead_status?: LeadStatus | string;
  cancel_reason?: string | null;
  internal_comment?: string;
  manager_edited_fields?: Record<
    string,
    { date: string; original_value: string | null }
  > | null;
  createdAt: string;
  updatedAt: string;
}

interface SearchFilters {
  name: string;
  phone: string;
  region: string;
  details: string;
}

interface TabCounts {
  selection: number;
  insurance: number;
  leasing: number;
}

const TAB_TYPES: LeadType[] = ['selection', 'leasing', 'insurance'];

const TAB_LABELS: Record<LeadType, string> = {
  selection: 'podbor',
  insurance: 'strahovanie',
  leasing: 'lizing',
};

const EXPORT_COLUMNS: Array<{ key: keyof Lead; label: string }> = [
  { key: 'id', label: 'ID' },
  { key: 'documentId', label: 'Document ID' },
  { key: 'type', label: 'Тип' },
  { key: 'lead_status', label: 'Статус' },
  { key: 'cancel_reason', label: 'Причина отмены' },
  { key: 'internal_comment', label: 'Внутренний комментарий' },
  { key: 'name', label: 'Имя' },
  { key: 'phone', label: 'Телефон' },
  { key: 'inn', label: 'ИНН' },
  { key: 'equipment', label: 'Оборудование' },
  { key: 'condition', label: 'Состояние' },
  { key: 'objectType', label: 'Тип объекта' },
  { key: 'region', label: 'Регион' },
  { key: 'amount', label: 'Сумма / бюджет' },
  { key: 'advance', label: 'Аванс' },
  { key: 'deadline', label: 'Срок' },
  { key: 'comment', label: 'Комментарий' },
  { key: 'form_source', label: 'Источник формы' },
  { key: 'consent_at', label: 'Согласие' },
  { key: 'referrer', label: 'Referrer' },
  { key: 'template', label: 'Шаблон' },
  { key: 'path', label: 'Страница' },
  { key: 'utm_source', label: 'utm_source' },
  { key: 'utm_campaign', label: 'utm_campaign' },
  { key: 'utm_term', label: 'utm_term' },
  { key: 'yclid', label: 'yclid' },
  { key: 'ym_uid', label: 'ym_uid' },
  { key: 'ym_cookies', label: 'ym_cookies' },
  { key: 'manager_edited_fields', label: 'Поля, изменённые менеджером' },
  { key: 'createdAt', label: 'Создана' },
  { key: 'updatedAt', label: 'Обновлена' },
];

const INITIAL_FILTERS: SearchFilters = {
  name: '',
  phone: '',
  region: '',
  details: '',
};

const INITIAL_COUNTS: TabCounts = {
  selection: 0,
  insurance: 0,
  leasing: 0,
};

const TAB_INDEX_BY_TYPE: Record<LeadType, number> = {
  selection: 0,
  leasing: 1,
  insurance: 2,
};

const getTabIndexFromType = (type?: string | null) => {
  if (type && type in TAB_INDEX_BY_TYPE) {
    return TAB_INDEX_BY_TYPE[type as LeadType];
  }

  return 0;
};

function a11yProps(index: number) {
  return {
    id: `lead-tab-${index}`,
    'aria-controls': `lead-tabpanel-${index}`,
  };
}

const textFieldSx = {
  '& .MuiOutlinedInput-root': {
    backgroundColor: 'white',
    fontSize: '14px',
  },
  '& .MuiInputBase-input': {
    padding: '8px 12px',
  },
};

const buildStrapiParams = (type: LeadType, filters: SearchFilters, withCount = false) => {
  const params: Record<string, string | number | boolean> = {
    sort: 'updatedAt:desc',
    'filters[type][$eq]': type,
  };

  if (withCount) {
    params['pagination[pageSize]'] = 1;
    params['pagination[withCount]'] = true;
  }

  if (filters.name) {
    params['filters[name][$containsi]'] = filters.name;
  }

  if (filters.phone) {
    params['filters[phone][$containsi]'] = filters.phone;
  }

  if (filters.region) {
    params['filters[region][$containsi]'] = filters.region;
  }

  if (filters.details) {
    const detailsField =
      type === 'selection' ? 'equipment' : type === 'insurance' ? 'objectType' : 'comment';
    params[`filters[${detailsField}][$containsi]`] = filters.details;
  }

  return params;
};

const hasActiveFilters = (filters: SearchFilters) =>
  Object.values(filters).some((value) => value.trim().length > 0);

const buildLeadsExcelRows = (rows: Lead[]) =>
  rows.map((row) => {
    const excelRow: Record<string, string | number> = {};

    for (const column of EXPORT_COLUMNS) {
      const value = row[column.key];
      if (column.key === 'lead_status') {
        excelRow[column.label] = getLeadStatusLabel(String(value ?? ''));
        continue;
      }
      if (column.key === 'cancel_reason') {
        excelRow[column.label] = getLeadCancelReasonLabel(
          value === null || value === undefined ? '' : String(value),
        );
        continue;
      }
      if (isLeadTranslatedExportField(column.key)) {
        excelRow[column.label] = formatLeadExportValue(column.key, value);
        continue;
      }
      if (column.key === 'manager_edited_fields') {
        excelRow[column.label] =
          value && typeof value === 'object' ? JSON.stringify(value) : '';
        continue;
      }
      excelRow[column.label] = value === null || value === undefined ? '' : String(value);
    }

    return excelRow;
  });

const downloadLeadsExcel = (rows: Lead[], fileName: string) => {
  const ws = XLSX.utils.json_to_sheet(buildLeadsExcelRows(rows));
  const wb = XLSX.utils.book_new();
  XLSX.utils.book_append_sheet(wb, ws, 'Заявки');
  XLSX.writeFile(wb, fileName);
};

export const LeadsAdmin = () => {
  const navigate = useNavigate();
  const [searchParams, setSearchParams] = useSearchParams();
  const [tabValue, setTabValue] = useState(() => getTabIndexFromType(searchParams.get('tab')));
  const [leads, setLeads] = useState<Lead[]>([]);
  const [tabCounts, setTabCounts] = useState<TabCounts>(INITIAL_COUNTS);
  const [loading, setLoading] = useState(true);
  const [selectedIds, setSelectedIds] = useState<Set<number>>(new Set());
  const [showDeleteDialog, setShowDeleteDialog] = useState(false);
  const [searchFilters, setSearchFilters] = useState<SearchFilters>(INITIAL_FILTERS);
  const [exporting, setExporting] = useState(false);

  const activeType = TAB_TYPES[tabValue];

  const fetchTabCounts = useCallback(async () => {
    try {
      const responses = await Promise.all(
        TAB_TYPES.map((type) =>
          apiClient.get('/leads', {
            params: buildStrapiParams(type, INITIAL_FILTERS, true),
          }),
        ),
      );

      setTabCounts({
        selection: responses[0].data.meta?.pagination?.total ?? 0,
        insurance: responses[1].data.meta?.pagination?.total ?? 0,
        leasing: responses[2].data.meta?.pagination?.total ?? 0,
      });
    } catch (error) {
      console.error('Ошибка при загрузке счётчиков заявок:', error);
    }
  }, []);

  const fetchLeads = useCallback(async (type: LeadType, filters: SearchFilters) => {
    try {
      setLoading(true);
      const response = await apiClient.get('/leads', {
        params: buildStrapiParams(type, filters),
      });
      setLeads(response.data.data || []);
    } catch (error) {
      console.error('Ошибка при загрузке заявок:', error);
      setLeads([]);
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    fetchTabCounts();
  }, [fetchTabCounts]);

  useEffect(() => {
    setTabValue(getTabIndexFromType(searchParams.get('tab')));
  }, [searchParams]);

  useEffect(() => {
    setSelectedIds(new Set());
    fetchLeads(activeType, searchFilters);
  }, [activeType, searchFilters, fetchLeads]);

  const handleFilterChange = (field: keyof SearchFilters, value: string) => {
    setSearchFilters((prev) => ({ ...prev, [field]: value }));
  };

  const handleTabChange = (_event: React.SyntheticEvent, newValue: number) => {
    setTabValue(newValue);
    setSearchParams({ tab: TAB_TYPES[newValue] }, { replace: true });
  };

  const handleSelectAll = (checked: boolean) => {
    if (checked) {
      setSelectedIds(new Set(leads.map((lead) => lead.id)));
      return;
    }

    setSelectedIds(new Set());
  };

  const handleSelectOne = (id: number, checked: boolean) => {
    const newSelected = new Set(selectedIds);

    if (checked) {
      newSelected.add(id);
    } else {
      newSelected.delete(id);
    }

    setSelectedIds(newSelected);
  };

  const isAllSelected = leads.length > 0 && leads.every((lead) => selectedIds.has(lead.id));
  const isIndeterminate = selectedIds.size > 0 && !isAllSelected;

  const refreshData = async () => {
    await Promise.all([fetchTabCounts(), fetchLeads(activeType, searchFilters)]);
  };

  const formatDate = (dateString: string) => {
    return new Date(dateString).toLocaleString('ru-RU', {
      year: 'numeric',
      month: 'short',
      day: 'numeric',
      hour: '2-digit',
      minute: '2-digit',
    });
  };

  const handleViewLead = (lead: Lead) => {
    navigate(`${ROUTE_PATHS.ADMIN_LEADS}/${lead.documentId || lead.id}`);
  };

  const handleDeleteLead = async (lead: Lead) => {
    const prompt = await confirm('Вы уверены, что хотите удалить эту заявку?');
    if (!prompt) return;

    try {
      await apiClient.delete(`/leads/${lead.documentId || lead.id}`);
      await refreshData();
    } catch (error) {
      console.error('Ошибка при удалении заявки:', error);
    }
  };

  const handleStatusChange = async (lead: Lead, lead_status: LeadStatus) => {
    if (lead_status === 'draft') {
      return;
    }

    try {
      await apiClient.put(`/leads/${lead.documentId || lead.id}`, {
        data: { lead_status },
      });

      setLeads((prev) =>
        prev.map((item) => (item.id === lead.id ? { ...item, lead_status } : item)),
      );
    } catch (error) {
      console.error('Ошибка при обновлении статуса:', error);
    }
  };

  const handleBulkDelete = () => {
    if (selectedIds.size > 0) {
      setShowDeleteDialog(true);
    }
  };

  const performBulkDelete = async () => {
    try {
      const deletePromises = Array.from(selectedIds).map((id) => {
        const lead = leads.find((item) => item.id === id);
        if (!lead) return Promise.resolve();

        return apiClient.delete(`/leads/${lead.documentId || lead.id}`);
      });

      await Promise.all(deletePromises);
      setSelectedIds(new Set());
      setShowDeleteDialog(false);
      await refreshData();
    } catch (error) {
      console.error('Ошибка при удалении заявок:', error);
    }
  };

  const getDetailsValue = (lead: Lead, type: LeadType) => {
    if (type === 'insurance') return lead.objectType || '-';
    if (type === 'leasing') return lead.equipment || lead.amount || '-';
    return lead.equipment || '-';
  };

  const fetchAllLeadsForExport = async (type: LeadType, filters: SearchFilters) => {
    const pageSize = 100;
    let page = 1;
    let pageCount = 1;
    const rows: Lead[] = [];

    while (page <= pageCount) {
      const response = await apiClient.get('/leads', {
        params: {
          ...buildStrapiParams(type, filters),
          'pagination[page]': page,
          'pagination[pageSize]': pageSize,
          'pagination[withCount]': true,
        },
      });

      rows.push(...(response.data.data || []));
      pageCount = response.data.meta?.pagination?.pageCount || 1;
      page += 1;
    }

    return rows;
  };

  const handleExportExcel = async () => {
    try {
      setExporting(true);
      const rows = await fetchAllLeadsForExport(activeType, searchFilters);
      if (rows.length === 0) {
        alert('Нет данных для экспорта');
        return;
      }

      const stamp = new Date().toISOString().slice(0, 19).replace(/[:T]/g, '-');
      downloadLeadsExcel(rows, `leads_${TAB_LABELS[activeType]}_${stamp}.xlsx`);
    } catch (error) {
      console.error('Ошибка при экспорте заявок:', error);
      alert('Не удалось выгрузить Excel');
    } finally {
      setExporting(false);
    }
  };

  const renderEmptyState = (type: LeadType) => (
    <Box className={styles.emptyState}>
      <Typography variant='h6' color='text.secondary' align='center'>
        Нет заявок с лендингов
      </Typography>
      <Typography variant='body2' color='text.secondary' align='center'>
        {type === 'selection' && 'Заявки на подбор будут отображаться здесь'}
        {type === 'insurance' && 'Заявки на страхование будут отображаться здесь'}
        {type === 'leasing' && 'Заявки на лизинг будут отображаться здесь'}
      </Typography>
    </Box>
  );

  const renderTable = (type: LeadType) => {
    if (loading) {
      return (
        <Box className={styles.loading}>
          <Typography>Загрузка...</Typography>
        </Box>
      );
    }

    if (leads.length === 0 && !hasActiveFilters(searchFilters)) {
      return renderEmptyState(type);
    }

    return (
      <Box>
        <Box
          style={{
            display: 'flex',
            justifyContent: 'space-between',
            alignItems: 'center',
            marginBottom: '20px',
          }}
        >
          <Box style={{ display: 'flex', alignItems: 'center', gap: '10px' }}>
            {selectedIds.size > 0 && (
              <>
                <Typography variant='body2' color='text.secondary'>
                  Выбрано: {selectedIds.size}
                </Typography>
                <ButtonCustom onClick={handleBulkDelete} className={styles.redBtn}>
                  Удалить выбранные
                </ButtonCustom>
              </>
            )}
          </Box>
        </Box>

        <TableContainer component={Box} className={styles.tableContainer}>
          <Table className={styles.table} sx={{ minWidth: 1200 }} aria-label='Список заявок'>
            <TableHead className={styles.tableHead}>
              <TableRow className={styles.tableHeadRow}>
                <TableCell
                  className={`${styles.tableHeadCellFirst} ${styles.tableHeadCell}`}
                  style={{ width: '60px' }}
                />
                <TableCell className={styles.tableHeadCell}>
                  <TextField
                    variant='outlined'
                    size='small'
                    placeholder='Имя'
                    value={searchFilters.name}
                    onChange={(e) => handleFilterChange('name', e.target.value)}
                    sx={textFieldSx}
                  />
                </TableCell>
                <TableCell className={`${styles.tableHeadCell} ${styles.tableCellMobile}`}>
                  <TextField
                    variant='outlined'
                    size='small'
                    placeholder='Телефон'
                    value={searchFilters.phone}
                    onChange={(e) => handleFilterChange('phone', e.target.value)}
                    sx={textFieldSx}
                  />
                </TableCell>

                <TableCell className={styles.tableHeadCell} align='center'>
                  Обновлено
                </TableCell>
                <TableCell className={styles.tableHeadCell} align='center'>
                  Статус
                </TableCell>
                <TableCell className={styles.tableHeadCell} align='center'>
                  Действия
                </TableCell>
              </TableRow>
              <TableRow className={styles.tableHeadRow}>
                <TableCell
                  className={`${styles.tableHeadCellFirst} ${styles.tableHeadCell}`}
                  style={{ width: '60px' }}
                >
                  <Checkbox
                    checked={isAllSelected}
                    indeterminate={isIndeterminate}
                    onChange={(e) => handleSelectAll(e.target.checked)}
                    color='primary'
                  />
                </TableCell>
                <TableCell className={styles.tableHeadCell}>Имя</TableCell>
                <TableCell className={`${styles.tableHeadCell} ${styles.tableCellMobile}`}>
                  Телефон
                </TableCell>
                <TableCell className={styles.tableHeadCell} align='center'>
                  Дата
                </TableCell>
                <TableCell className={styles.tableHeadCell} align='center'>
                  -
                </TableCell>
                <TableCell className={styles.tableHeadCell} align='center'>
                  -
                </TableCell>
              </TableRow>
            </TableHead>
            <TableBody className={styles.tableBody}>
              {leads.length === 0 ? (
                <TableRow>
                  <TableCell colSpan={8} align='center' style={{ padding: '40px 20px' }}>
                    <Typography variant='h6' color='text.secondary'>
                      По вашему запросу ничего не найдено
                    </Typography>
                    <Typography variant='body2' color='text.secondary' style={{ marginTop: '8px' }}>
                      Попробуйте изменить критерии поиска
                    </Typography>
                  </TableCell>
                </TableRow>
              ) : (
                leads.map((lead) => (
                  <TableRow
                    key={lead.id}
                    className={styles.tableBodyRow}
                    sx={{
                      '& td, & th': { border: 0 },
                      marginTop: '10px',
                    }}
                  >
                    <TableCell
                      className={`${styles.tableBodyCellFirst} ${styles.tableBodyCell}`}
                      onClick={(e) => e.stopPropagation()}
                    >
                      <Checkbox
                        checked={selectedIds.has(lead.id)}
                        onChange={(e) => handleSelectOne(lead.id, e.target.checked)}
                        color='primary'
                      />
                    </TableCell>
                    <TableCell className={styles.tableBodyCell} component='th' scope='row'>
                      {lead.name || '-'}
                    </TableCell>
                    <TableCell className={`${styles.tableBodyCell} ${styles.tableCellMobile}`}>
                      {lead.phone || '-'}
                    </TableCell>
                    <TableCell className={styles.tableBodyCell} align='center'>
                      <Typography variant='caption'>{formatDate(lead.updatedAt)}</Typography>
                    </TableCell>
                    <TableCell
                      className={styles.tableBodyCell}
                      align='center'
                      onClick={(e) => e.stopPropagation()}
                    >
                      <FormControl size='small' className={styles.statusSelect}>
                        <Select
                          value={normalizeLeadStatus(lead.lead_status)}
                          onChange={(e) =>
                            handleStatusChange(lead, e.target.value as LeadStatus)
                          }
                          className={clsx(
                            styles.statusSelectField,
                            styles[getLeadStatusClassName(lead.lead_status)],
                          )}
                          renderValue={(value) => getLeadStatusLabel(value)}
                        >
                          {normalizeLeadStatus(lead.lead_status) === 'draft' && (
                            <MenuItem value='draft' disabled>
                              {getLeadStatusLabel('draft')}
                            </MenuItem>
                          )}
                          {EDITABLE_LEAD_STATUSES.map((status) => (
                            <MenuItem key={status} value={status}>
                              {getLeadStatusLabel(status)}
                            </MenuItem>
                          ))}
                        </Select>
                      </FormControl>
                    </TableCell>
                    <TableCell
                      className={styles.tableBodyCell}
                      align='center'
                      onClick={(e) => e.stopPropagation()}
                    >
                      <Box style={{ display: 'flex', gap: '8px', justifyContent: 'center' }}>
                        <Button
                          variant='outlined'
                          color='info'
                          onClick={() => handleViewLead(lead)}
                          size='small'
                        >
                          Просмотр
                        </Button>
                      </Box>
                    </TableCell>
                  </TableRow>
                ))
              )}
            </TableBody>
          </Table>
        </TableContainer>
      </Box>
    );
  };

  return (
    <Box className={styles.container}>
      <DashboardHeaderBackLink link={ROUTE_PATHS.ADMIN_LAST} title={'Заявки с сайта'} />

      <Box className={styles.tabsContainer}>
        <Tabs
          value={tabValue}
          onChange={handleTabChange}
          aria-label='lead applications tabs'
          className={styles.tabs}
        >
          <Tab label={`Техника (${tabCounts.selection})`} {...a11yProps(0)} />
          <Tab label={`Лизинг (${tabCounts.leasing})`} {...a11yProps(1)} />
          <Tab label={`Страхование (${tabCounts.insurance})`} {...a11yProps(2)} />
        </Tabs>
        <button
          type='button'
          className={styles.exportBtn}
          onClick={handleExportExcel}
          disabled={exporting || loading || leads.length === 0}
        >
          {exporting ? 'Выгрузка...' : 'Выгрузить в Excel'}
        </button>
      </Box>

      <Box sx={{ p: 3 }}>{renderTable(activeType)}</Box>
    </Box>
  );
};
